/**
 * SegmentlyInternalAnalyticsEditor - Configuration editor for first-party analytics.
 *
 * Transport tuning (HTTP mode): accumulation window, batch size, delivery
 * guarantee, immediate-event list, and the early-journey flush rule. Invalid
 * values are blocked here; the WebShell adapter additionally clamps anything
 * that reaches runtime through other paths.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type {
  PlatformConfig,
  SegmentlyInternalAnalyticsConfig,
} from '../../types/analytics.types';

interface SegmentlyInternalAnalyticsEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
}

const FLUSH_INTERVAL_MIN_MS = 100;
const FLUSH_INTERVAL_MAX_MS = 60000;
const BATCH_SIZE_MIN = 1;
const BATCH_SIZE_MAX = 25;
const EARLY_SCREENS_MIN = 0;
const EARLY_SCREENS_MAX = 10;

function parseImmediateEvents(value: string): string[] {
  return value
    .split(',')
    .map(item => item.trim())
    .filter(item => item.length > 0);
}

export const SegmentlyInternalAnalyticsEditor: React.FC<SegmentlyInternalAnalyticsEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
}) => {
  const incomingConfig = config as SegmentlyInternalAnalyticsConfig;
  const [localConfig, setLocalConfig] = useState<SegmentlyInternalAnalyticsConfig>(incomingConfig);
  const [immediateEventsText, setImmediateEventsText] = useState(
    (incomingConfig.immediateEvents ?? []).join(', ')
  );

  useEffect(() => {
    setLocalConfig(incomingConfig);
    setImmediateEventsText((incomingConfig.immediateEvents ?? []).join(', '));
  }, [incomingConfig]);

  const mode = localConfig.mode ?? 'http';
  const earlyJourney = localConfig.earlyJourney ?? {};
  const earlyMode = earlyJourney.mode ?? 'immediate';

  const flushIntervalError = useMemo(() => {
    const value = localConfig.flushIntervalMs;
    if (value === undefined) return null;
    if (!Number.isFinite(value) || value < FLUSH_INTERVAL_MIN_MS || value > FLUSH_INTERVAL_MAX_MS) {
      return `Must be a number between ${FLUSH_INTERVAL_MIN_MS} and ${FLUSH_INTERVAL_MAX_MS} ms.`;
    }
    return null;
  }, [localConfig.flushIntervalMs]);

  const batchSizeError = useMemo(() => {
    const value = localConfig.flushQueueSize;
    if (value === undefined) return null;
    if (!Number.isInteger(value) || value < BATCH_SIZE_MIN || value > BATCH_SIZE_MAX) {
      return `Must be an integer between ${BATCH_SIZE_MIN} and ${BATCH_SIZE_MAX} (server request cap).`;
    }
    return null;
  }, [localConfig.flushQueueSize]);

  const earlyScreensError = useMemo(() => {
    const value = earlyJourney.screens;
    if (value === undefined) return null;
    if (!Number.isInteger(value) || value < EARLY_SCREENS_MIN || value > EARLY_SCREENS_MAX) {
      return `Must be an integer between ${EARLY_SCREENS_MIN} and ${EARLY_SCREENS_MAX}.`;
    }
    return null;
  }, [earlyJourney.screens]);

  const earlyIntervalError = useMemo(() => {
    if (earlyMode !== 'interval') return null;
    const value = earlyJourney.intervalMs;
    if (value === undefined) return 'Required when early mode is "Short interval".';
    if (!Number.isFinite(value) || value < 0 || value > FLUSH_INTERVAL_MAX_MS) {
      return `Must be a number between 0 and ${FLUSH_INTERVAL_MAX_MS} ms.`;
    }
    return null;
  }, [earlyMode, earlyJourney.intervalMs]);

  const isValid = !flushIntervalError && !batchSizeError && !earlyScreensError && !earlyIntervalError;

  const applyChange = useCallback(
    (nextConfig: SegmentlyInternalAnalyticsConfig) => {
      setLocalConfig(nextConfig);
      if (!showSaveButton) {
        onChange(nextConfig, name);
      }
    },
    [showSaveButton, onChange, name]
  );

  const handleNumberChange = useCallback(
    (field: 'flushIntervalMs' | 'flushQueueSize', raw: string) => {
      const next = { ...localConfig };
      if (raw.trim() === '') {
        delete next[field];
      } else {
        next[field] = Number(raw);
      }
      applyChange(next);
    },
    [localConfig, applyChange]
  );

  const handleEarlyChange = useCallback(
    (patch: Partial<NonNullable<SegmentlyInternalAnalyticsConfig['earlyJourney']>>, removeKeys: string[] = []) => {
      const nextEarly: Record<string, unknown> = { ...earlyJourney, ...patch };
      removeKeys.forEach(key => delete nextEarly[key]);
      applyChange({ ...localConfig, earlyJourney: nextEarly });
    },
    [earlyJourney, localConfig, applyChange]
  );

  const handleImmediateEventsChange = useCallback(
    (raw: string) => {
      setImmediateEventsText(raw);
      const next = { ...localConfig };
      if (raw.trim() === '') {
        delete next.immediateEvents;
      } else {
        next.immediateEvents = parseImmediateEvents(raw);
      }
      applyChange(next);
    },
    [localConfig, applyChange]
  );

  const handleSave = useCallback(() => {
    if (onSave && isValid) {
      onSave(localConfig, name);
    }
  }, [onSave, isValid, localConfig, name]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Segmently Analytics enables first-party funnel event collection for the
        native Reports dashboard and canvas insights overlay. Published funnels
        do not send first-party analytics until this integration is enabled.
      </Alert>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reports and canvas insights are also controlled by the project billing
        capability <strong>funnel_insights.access</strong>. The integration
        controls data collection; the capability controls who can view reports
        and overlay metrics.
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Ingest mode</InputLabel>
        <Select
          value={mode}
          label="Ingest mode"
          disabled={disabled || readOnly}
          data-testid="segmently-internal-mode-select"
          onChange={(event) => applyChange({ ...localConfig, mode: event.target.value as SegmentlyInternalAnalyticsConfig['mode'] })}
        >
          <MenuItem value="http">HTTP ingest</MenuItem>
          <MenuItem value="log">Console log</MenuItem>
          <MenuItem value="memory">In-memory test sink</MenuItem>
        </Select>
      </FormControl>

      {mode === 'http' && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Event delivery optimization
          </Typography>

          <TextField
            label="Flush interval (ms)"
            type="number"
            value={localConfig.flushIntervalMs ?? ''}
            onChange={(e) => handleNumberChange('flushIntervalMs', e.target.value)}
            disabled={disabled || readOnly}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="5000"
            error={!!flushIntervalError}
            helperText={flushIntervalError ?? 'Accumulation window for normal events. Default 5000 ms.'}
            inputProps={{ 'data-testid': 'segmently-internal-flush-interval-input' }}
          />

          <TextField
            label="Max events per request"
            type="number"
            value={localConfig.flushQueueSize ?? ''}
            onChange={(e) => handleNumberChange('flushQueueSize', e.target.value)}
            disabled={disabled || readOnly}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="25"
            error={!!batchSizeError}
            helperText={batchSizeError ?? 'Batch size triggering an immediate flush. Default 25 (server cap).'}
            inputProps={{ 'data-testid': 'segmently-internal-batch-size-input' }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Delivery guarantee</InputLabel>
            <Select
              value={localConfig.deliveryMode ?? 'retry'}
              label="Delivery guarantee"
              disabled={disabled || readOnly}
              data-testid="segmently-internal-delivery-mode-select"
              onChange={(event) => applyChange({ ...localConfig, deliveryMode: event.target.value as 'retry' | 'best_effort' })}
            >
              <MenuItem value="retry">Retry with backoff (recommended)</MenuItem>
              <MenuItem value="best_effort">Best effort (single attempt)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Immediate events (comma-separated)"
            value={immediateEventsText}
            onChange={(e) => handleImmediateEventsChange(e.target.value)}
            disabled={disabled || readOnly}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="PurchaseFooterButtonPressed, StripePurchase"
            helperText="Canonical event names flushed without waiting. Empty = built-in defaults (purchase, buy-intent, exit signals)."
            inputProps={{ 'data-testid': 'segmently-internal-immediate-events-input' }}
          />

          <TextField
            label="Early-journey screens"
            type="number"
            value={earlyJourney.screens ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw.trim() === '') {
                handleEarlyChange({}, ['screens']);
              } else {
                handleEarlyChange({ screens: Number(raw) });
              }
            }}
            disabled={disabled || readOnly}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="2"
            error={!!earlyScreensError}
            helperText={earlyScreensError ?? 'Events from the first N screens of a journey are delivered faster. 0 disables. Default 2.'}
            inputProps={{ 'data-testid': 'segmently-internal-early-screens-input' }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Early-journey delivery</InputLabel>
            <Select
              value={earlyMode}
              label="Early-journey delivery"
              disabled={disabled || readOnly}
              data-testid="segmently-internal-early-mode-select"
              onChange={(event) => {
                const nextMode = event.target.value as 'immediate' | 'interval';
                if (nextMode === 'immediate') {
                  handleEarlyChange({ mode: 'immediate' }, ['intervalMs']);
                } else {
                  handleEarlyChange({ mode: 'interval' });
                }
              }}
            >
              <MenuItem value="immediate">Immediate</MenuItem>
              <MenuItem value="interval">Short interval</MenuItem>
            </Select>
          </FormControl>

          {earlyMode === 'interval' && (
            <TextField
              label="Early flush interval (ms)"
              type="number"
              value={earlyJourney.intervalMs ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw.trim() === '') {
                  handleEarlyChange({}, ['intervalMs']);
                } else {
                  handleEarlyChange({ intervalMs: Number(raw) });
                }
              }}
              disabled={disabled || readOnly}
              fullWidth
              sx={{ mb: 2 }}
              placeholder="1000"
              error={!!earlyIntervalError}
              helperText={earlyIntervalError ?? 'Accumulation window for early-journey events.'}
              inputProps={{ 'data-testid': 'segmently-internal-early-interval-input' }}
            />
          )}
        </>
      )}

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || readOnly || !isValid}
            data-testid="segmently-internal-save-btn"
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
